import React, { useState, useCallback } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import DateTimePicker from './DateTimePicker';
import '../styles/FileUpload.css';

const FileUpload = ({ onFileSelect, onAnalysisComplete }) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [targetDateTime, setTargetDateTime] = useState(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const validateFile = (file) => {
    if (!file.name.toLowerCase().endsWith('.gpx')) {
      setError('Si us plau, puja un fitxer GPX vàlid');
      return false;
    }
    if (file.size > 10 * 1024 * 1024) { // 10MB max
      setError('El fitxer és massa gran (màxim 10MB)');
      return false;
    }
    return true;
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setError(null);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
      }
    }
  }, []);

  const handleChange = (e) => {
    e.preventDefault();
    setError(null);
    
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setProgress(0);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      if (targetDateTime) {
        formData.append('target_datetime', targetDateTime);
      }

      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/analyze`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setProgress(100);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error processant el fitxer');
      }

      const data = await response.json();
      
      setTimeout(() => {
        setUploading(false);
        onAnalysisComplete(data);
      }, 500);

    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'Error processant el fitxer GPX');
      setUploading(false);
      setProgress(0);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setError(null);
    setProgress(0);
    setTargetDateTime(null);
  };

  return (
    <div className="file-upload-container">
      {!selectedFile ? (
        <div
          className={`upload-area ${dragActive ? 'drag-active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <Upload size={48} className="upload-icon" />
          <h3>Puja el teu fitxer GPX</h3>
          <p>Arrossega i deixa anar o fes clic per seleccionar</p>
          
          <input
            type="file"
            id="file-input"
            className="file-input"
            accept=".gpx"
            onChange={handleChange}
          />
          <label htmlFor="file-input" className="upload-button">
            Selecciona fitxer
          </label>

          <div className="file-info">
            <p>Formats acceptats: .gpx</p>
            <p>Mida màxima: 10MB</p>
          </div>
        </div>
      ) : (
        <div className="file-selected">
          <div className="file-details">
            <FileText size={40} className="file-icon" />
            <div className="file-info-text">
              <h4>{selectedFile.name}</h4>
              <p>{(selectedFile.size / 1024).toFixed(2)} KB</p>
            </div>
            {!uploading && (
              <button onClick={handleReset} className="reset-button">
                ✕
              </button>
            )}
          </div>

          {!uploading && (
            <>
              <DateTimePicker 
                value={targetDateTime}
                onChange={setTargetDateTime}
              />
              
              <button onClick={handleUpload} className="analyze-button">
                Analitzar Ruta
              </button>
            </>
          )}

          {uploading && (
            <div className="upload-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="upload-status">Analitzant ruta...</p>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="error-message">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default FileUpload;